import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, Check, ShieldCheck, Banknote, AlertTriangle, TrendingUp,
  Clock4, Flame, Sparkles, ClipboardCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, ShieldMark } from "@/components/Logo";
import { SiteHeader } from "@/components/SiteHeader";
import { Seo, SITE_URL } from "@/components/Seo";
import { getConfig } from "@/lib/api";

const FACT_ICONS = [Banknote, ShieldCheck, AlertTriangle, TrendingUp];

export default function LandingTemplate({ content }) {
  const nav = useNavigate();
  const [founders, setFounders] = useState(null);
  useEffect(() => {
    getConfig()
      .then((c) => setFounders({ remaining: c.founders_remaining, limit: c.founder_limit }))
      .catch(() => {});
  }, []);

  const startForm = () => nav(content.formQuery ? `/form${content.formQuery}` : "/form");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: content.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: content.breadcrumb, item: `${SITE_URL}${content.path}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title={content.seo.title}
        description={content.seo.description}
        path={content.path}
        keywords={content.seo.keywords}
        noindex
        jsonLd={jsonLd}
      />

      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-60 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200/80 text-xs uppercase tracking-widest text-slate-600 mb-4 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full brand-gradient-bg" />
              {content.eyebrow}
            </div>

            {founders && founders.remaining > 0 && (
              <div
                data-testid="landing-founder-offer"
                className="block sm:inline-flex items-center gap-2 mb-6 sm:ml-3 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-800 w-fit"
              >
                <Flame className="h-3.5 w-3.5 text-amber-500 inline" />{" "}
                Einführungspreis 29 € statt 39 € · Limitiert auf die ersten {founders.limit} Gründer · noch {founders.remaining} frei
              </div>
            )}

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] break-words hyphens-auto"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <span className="text-slate-900">{content.h1.pre}</span>
              <span className="brand-gradient-text">{content.h1.grad}</span>
              <span className="text-slate-900">{content.h1.post}</span>
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">{content.sub}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="hero-start-btn"
                onClick={startForm}
                className="pulse-cta h-auto min-h-14 py-3 px-6 sm:px-8 text-sm sm:text-base font-semibold rounded-xl shadow-lg brand-gradient-bg text-white hover:opacity-95 whitespace-normal text-center leading-snug w-full sm:w-auto"
              >
                {content.ctaLabel}
                <ArrowRight className="ml-2 h-5 w-5 shrink-0" />
              </Button>
              <div className="flex items-center gap-2 text-sm text-slate-500 px-2">
                <ShieldCheck className="h-4 w-4" style={{ color: "#10C98D" }} />
                {content.ctaNote}
              </div>
            </div>

            <p className="mt-4 text-xs text-slate-500 max-w-xl leading-relaxed">{content.legal}</p>

            <div className="mt-10 grid grid-cols-2 gap-3 max-w-md">
              {content.stats.map(([num, label]) => (
                <div key={label} className="rounded-2xl bg-white border border-slate-200/70 p-4 shadow-sm">
                  <div className="text-2xl font-extrabold brand-gradient-text" style={{ fontFamily: "Outfit" }}>{num}</div>
                  <div className="text-xs text-slate-600 mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT checklist card */}
          <div className="lg:col-span-5 animate-fade-in-up">
            <div className="relative">
              <div className="absolute -inset-2 brand-gradient-bg opacity-20 blur-2xl rounded-3xl" />
              <div className="relative bg-white rounded-3xl border border-slate-200/70 p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-5">
                  <div className="text-xs uppercase tracking-widest text-slate-500">{content.checklistTitle}</div>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">10+ Seiten</span>
                </div>
                <ul className="space-y-3">
                  {content.checklist.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="mt-0.5 h-5 w-5 rounded-full brand-gradient-bg flex items-center justify-center shrink-0">
                        <Check className="h-3 w-3 text-white" />
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-slate-200 flex items-end justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Einführungspreis</div>
                    <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>29,00 €</div>
                  </div>
                  <div className="text-xs text-slate-500 text-right">Sofort-Download</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FACTS */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-slate-900" style={{ fontFamily: "Outfit" }}>
          {content.factsTitle}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {content.facts.map((f, i) => {
            const Icon = FACT_ICONS[i % FACT_ICONS.length];
            return (
              <div key={f.title} className="rounded-2xl bg-white border border-slate-200/70 p-5">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-3 bg-emerald-50">
                  <Icon className="h-5 w-5" style={{ color: "#10C98D" }} />
                </div>
                <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{f.title}</div>
                <p className="text-sm text-slate-600 mt-1.5">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-slate-900" style={{ fontFamily: "Outfit" }}>
          {content.stepsTitle}
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {content.steps.map((s, i) => (
            <div key={s.title} className="relative pl-14">
              <div
                className="absolute left-0 top-0 h-10 w-10 rounded-xl text-white font-bold flex items-center justify-center brand-gradient-bg shadow-md"
                style={{ fontFamily: "Outfit" }}
              >
                {i + 1}
              </div>
              <h4 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{s.title}</h4>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DARK CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-slate-900" />
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full opacity-30 brand-gradient-bg blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full opacity-25 bg-emerald-400 blur-3xl" />
          <div className="relative p-10 text-white grid md:grid-cols-[auto,1fr,auto] gap-6 items-center">
            <div className="hidden md:block"><ShieldMark size={64} /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4" /> Schritt 1: Kostenlose Score-Tendenz (Report danach 29 €)
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{content.darkTitle}</h2>
              <p className="text-white/70 mt-3 max-w-2xl">{content.darkText}</p>
            </div>
            <Button
              data-testid="cta-bottom-btn"
              onClick={startForm}
              className="h-14 px-7 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95"
            >
              <Sparkles className="h-5 w-5 mr-2" /> Jetzt starten
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="text-2xl font-bold mb-6 text-slate-900" style={{ fontFamily: "Outfit" }}>Häufige Fragen</h2>
        <div className="space-y-3">
          {content.faq.map((f) => (
            <details key={f.q} className="group bg-white rounded-2xl border border-slate-200/70 p-5">
              <summary className="cursor-pointer font-semibold text-slate-900 list-none flex justify-between items-center">
                <span style={{ fontFamily: "Outfit" }}>{f.q}</span>
                <span className="text-emerald-600 group-open:rotate-45 transition-transform">＋</span>
              </summary>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200/70 bg-white/60">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-slate-500 grid sm:grid-cols-2 gap-4 items-start">
          <div className="space-y-2">
            <Logo size="sm" />
            <p className="max-w-md leading-relaxed">
              Gründio bietet eine automatisierte Orientierungshilfe und KI-gestützte Erstbewertung.
              <b> Keine Rechts- oder Steuerberatung</b>.
            </p>
          </div>
          <div className="sm:text-right">© {new Date().getFullYear()} Gründio · Stand 2026</div>
        </div>
      </footer>
    </div>
  );
}
